import {
  BookmarkSimpleIcon,
  ChatCircleIcon,
  HeartIcon,
  PaperPlaneTiltIcon,
  SealCheckIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  type PlatformPreviewBaseProps,
  compactNumber,
  previewTimestampLabel,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Instagram's real feed fold is commonly cited around 125 characters (roughly
// 2-3 lines) before the caption collapses behind "more".
const FOLD_LENGTH = 125;

/**
 * Instagram Post — square image, header→image→actions→likes→caption→time.
 *
 * ── THIS HEADER USED TO SAY THE OPPOSITE ──────────────────────────────────
 * It read: _"Mora-native replica… NO platform-coloured chrome — uses semantic
 * tokens only."_ That was the stated policy for the whole organic family, and
 * it directly contradicted `platform-tokens.ts`'s ruling for the paid
 * replicas — _"the platform's own appearance IS the information."_ Two
 * intentional decisions, made at different times, neither aware of the other.
 * The founder settled it in favour of the platform, so the sentence is gone
 * rather than left to be quoted back as policy. (`FacebookPost.tsx` moved
 * first and carries the fuller reasoning; `LinkedInPost.tsx` followed.)
 *
 * ── TWO THINGS SPECIFIC TO INSTAGRAM, BOTH WORTH KNOWING ──────────────────
 * 1. **Instagram's dark mode is PURE BLACK** (`#000000` card AND page), so on
 *    Mora's near-black surfaces this card has almost no visible edge. That is
 *    faithful — a real Instagram feed has no card outlines either, only
 *    dividers — but it means the `--preview-stage` token, which was tuned so
 *    a card reads as sitting ON something, does not achieve that here. The
 *    paid `InstagramAdPreview` already shipped with this exact skin and shows
 *    the same thing, so this conversion makes the two agree rather than
 *    introducing a new condition. Recorded as an open question in
 *    `HANDOFF-AD-PREVIEW-SYSTEM-2026-08-21.md` §5 — it needs a sourced
 *    Instagram divider colour or a stage change, NOT a lightened card.
 * 2. **This skin has no distinct secondary ink.** `--platform-instagram-*-
 *    secondary-text` equals `primary-text` in BOTH themes, because the Figma
 *    frames evidently carried no grey-text sample. Mapping the timestamp and
 *    the "more" affordance to `secondaryText` would therefore render them at
 *    FULL contrast, louder than Instagram draws them. They use the platform's
 *    own primary ink at reduced opacity instead — derived from a sourced
 *    value rather than an invented grey.
 */
export const InstagramPost: React.FC<PlatformPreviewBaseProps> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  caption,
  imageUrl,
  imageUrls,
  imageAspectRatios,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  aspectRatio,
  focalX,
  focalY,
  zoom,
  timestamp,
  engagement,
  isLoading = false,
  emptyHint,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');
  const likes = engagement?.likes;
  const time = previewTimestampLabel(timestamp);

  // Carousel: the SAME shared hook every other platform-preview replica uses
  // (Facebook/LinkedIn/Threads/X) — this used to be its own hand-rolled copy
  // here, which is how the contrast fix on the nav buttons (shadow-elevation-1)
  // would have silently missed Instagram, the platform most posts preview as.
  const { currentImage, currentVideoUrl, currentVideoPoster, carouselOverlay, slides, safeActive } =
    useCarousel(imageUrls, imageUrl, {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    });
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, safeActive),
    1,
  );
  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(caption, FOLD_LENGTH);
  // Reads the theme off `<html>`; `useTheme()` throws outside a ThemeProvider
  // and this renders in the calendar hover card, the inspector, onboarding's
  // ready step and the style guide.
  const isDarkMode = useResolvedDarkMode();
  const skin = PLATFORM_SKINS.instagram[isDarkMode ? 'dark' : 'light'];
  /** See the file header, point 2: this skin has no distinct secondary ink. */
  const mutedInk = { color: skin.primaryText, opacity: 0.6 } as const;

  return (
    <article
      className={cn('w-full overflow-hidden rounded-xl', className)}
      data-platform="instagram-post"
      style={{ backgroundColor: skin.cardBackground }}
      aria-busy={isLoading}
    >
      <header className="flex items-center gap-3 px-3 py-2.5">
        <PreviewAvatar
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          className="size-8 rounded-full"
          textClassName="text-detail font-semibold tracking-tight"
        />
        <div className="flex-1 min-w-0 flex items-center gap-1">
          {/* Instagram's feed header shows the @username (handle), NOT a
              separate display name — same value as the caption byline below. */}
          <p className="truncate text-sm font-semibold" style={{ color: skin.primaryText }}>
            {handle}
          </p>
          {isVerified ? (
            <SealCheckIcon
              className="size-3.5 shrink-0 text-platform-instagram-verified"
              weight="fill"
              aria-label="Verified"
            />
          ) : null}
        </div>
      </header>

      <MediaSlot
        ratio={resolvedAspect}
        bg="muted"
        imageUrl={currentImage}
        videoUrl={currentVideoUrl ?? videoUrl}
        videoPoster={currentVideoPoster ?? videoPoster}
        focalX={focalX}
        focalY={focalY}
        zoom={zoom}
        isLoading={isLoading}
        emptyHint={emptyHint}
        alt={caption ?? `${brandName} post`}
        overlay={
          mediaOverlay ? (
            <>
              {carouselOverlay}
              {mediaOverlay}
            </>
          ) : (
            carouselOverlay
          )
        }
      />

      {slides ? (
        <div className="flex items-center justify-center gap-1 pt-2" aria-hidden>
          {slides.map((_, i) => (
            <span
              key={i}
              style={{ backgroundColor: skin.linkText ?? skin.primaryText }}
              className={cn(
                'size-1.5 rounded-full transition-opacity',
                i === safeActive ? 'opacity-100' : 'opacity-25',
              )}
            />
          ))}
        </div>
      ) : null}

      <div className="flex items-center justify-between px-3 pt-3 pb-1.5">
        <div className="flex items-center gap-4">
          <HeartIcon
            className="size-6"
            weight="regular"
            aria-hidden
            style={{ color: skin.primaryText }}
          />
          <ChatCircleIcon
            className="size-6"
            weight="regular"
            aria-hidden
            style={{ color: skin.primaryText }}
          />
          <PaperPlaneTiltIcon
            className="size-6"
            weight="regular"
            aria-hidden
            style={{ color: skin.primaryText }}
          />
        </div>
        <BookmarkSimpleIcon
          className="size-6"
          weight="regular"
          aria-hidden
          style={{ color: skin.primaryText }}
        />
      </div>

      {likes != null ? (
        <p className="px-3 text-detail font-semibold" style={{ color: skin.primaryText }}>
          {compactNumber(likes)} likes
        </p>
      ) : null}

      {isLoading && !caption ? (
        // Reserves the caption's two-line footprint so the real caption doesn't
        // pop the layout when it arrives (e.g. the onboarding done-screen sample
        // post, which renders isLoading with no caption text yet).
        <div className="px-3 pt-1.5 pb-2 space-y-1.5">
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ) : caption ? (
        <div className="px-3 pt-1.5 pb-2">
          <p
            className="whitespace-pre-wrap break-words text-detail leading-relaxed"
            style={{ color: skin.primaryText }}
          >
            <span className="font-semibold">{handle}</span>{' '}
            <span>
              {visible}
              {needsFold && !expanded ? (
                <>
                  {'… '}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="transition-opacity hover:underline hover:opacity-100"
                    style={mutedInk}
                  >
                    more
                  </button>
                </>
              ) : null}
            </span>
          </p>
        </div>
      ) : null}

      <p className="px-3 pb-3 text-micro uppercase tracking-wider" style={mutedInk}>
        {time}
      </p>
    </article>
  );
};
